import { useRef, useState } from "react";
import { ScrollView, View } from "react-native";
import { SectionCard } from "../molecules/SectionCard";
import {
  AlertCircleIcon,
  CalendarDaysIcon,
  ClockIcon,
  FilterIcon,
} from "ui/gluestack/icon";
import { FilterMenu } from "ui/organisms/FilterMenu";
import { PopupMenu } from "app/components/molecules/PopupMenu";
import { useHandleHandoverFilters } from "../hooks/useHandleHandoverFilters";
import { FilterSummary } from "../organisms/FilterSummary";
import { useHandleShiftHandoverDashboard } from "../../shiftHandover/hooks/useHandleShiftHandoverDashboard";
import LoadingSpinner from "ui/atoms/spinner/LoadingSpinner";
import { useRouter } from "expo-router";
import { CalendarEventModal } from "../../calendar/modals/CalendarEventModal";
import { UrgentConcernModel } from "../../../services/api/types/shiftHandover/models/UrgentConcernModel";
import { NoItemsComponent } from "ui/atoms/noItems";
import { OverdueTaskItem } from "../molecules/OverdueTaskItem";
import { useCompleteAppointmentMutation } from "../../../services/api/calendarApi";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { SingleHandoverAppointmentModel } from "../../../services/api/types/shiftHandover/models/SingleHandoverAppointmentModel";
import { UpcomingTaskItem } from "../molecules/UpcomingTaskItem";
import { ConcernTaskItem } from "../molecules/ConcernTaskItem";
import { UrgentConcernStatusEnum } from "../../../services/api/types/shiftHandover/enums/UrgentConcernStatusEnum";
import { useUpdateUrgentConcernStatusMutation } from "../../../services/api/shiftHandoverApi";
import { TodayTaskItem } from "../molecules/TodayTaskItem";
import { Button, ButtonIcon, ButtonText } from "ui/gluestack/button";
import { useHandleShiftHandoverAppointments } from "app/features/shiftHandover/hooks/useHandleShiftHandoverAppointments";

export function HandoverDashboardTemplate() {
  const { navigate } = useRouter();

  const [completeAppointment] = useCompleteAppointmentMutation();
  const [updateUrgentConcernState] = useUpdateUrgentConcernStatusMutation();
  const [isCompleted, setIsCompleted] = useState<Record<string, boolean>>({});
  const { showErrorToast, showNewToast } = useDisplayToast();
  const { ref, handleAppointmentRowPress } =
    useHandleShiftHandoverAppointments();

  const handleUrgentConcernRowPress = (residentId: number) => {
    return navigate(`/residents/${residentId}/handover`);
  };

  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuIconRef = useRef<View>(null);

  const {
    floors,
    selectedFloorIds,
    selectedRoomIds,
    toggleFloor,
    toggleRoom,
    unassignedSelected,
    handleToggleUnassigned,
    areFloorsLoading,
  } = useHandleHandoverFilters();

  const { data, isLoading } = useHandleShiftHandoverDashboard({
    floorIds: selectedFloorIds,
    roomIds: selectedRoomIds,
    unassigned: unassignedSelected,
  });

  const openMenu = () => {
    menuIconRef.current?.measure((_fx, _fy, _width, height, px, py) => {
      setMenuPosition({ top: py + height + 5, left: px });
      setShowMenu(true);
    });
  };

  if (areFloorsLoading || isLoading) {
    return (
      <View className="flex-1 items-center my-10">
        <LoadingSpinner />
      </View>
    );
  }

  const handleMarkAsDone = async (
    appointment: SingleHandoverAppointmentModel,
  ) => {
    try {
      if (!appointment?.starts_at) {
        throw new Error("Unable to complete. Missing occurrence start date");
      }
      await completeAppointment({
        id: appointment.id!,
        body: {
          occurrence_date: appointment.starts_at,
        },
      }).unwrap();

      setIsCompleted((prevState) => ({
        ...prevState,
        [appointment.id!]: true,
      }));

      showNewToast({
        title: "Appointment complete",
        message: "The appointment has been completed successfully.",
        type: "success",
      });
    } catch (e) {
      showErrorToast(e);
    }
  };

  const handleMarkConcernAsDone = async (concern: UrgentConcernModel) => {
    try {
      await updateUrgentConcernState({
        params: { id: concern.id! },
        body: { status: UrgentConcernStatusEnum.RESOLVED },
      }).unwrap();

      setIsCompleted((prevState) => ({
        ...prevState,
        [concern.id!]: true,
      }));

      showNewToast({
        title: "Urgent concern resolved",
        message: "The concern has been resolved successfully.",
        type: "success",
      });
    } catch (e) {
      showErrorToast(e);
    }
  };

  return (
    <View className="px-dashboard flex-1">
      <View className="sm:flex-row sm:items-center justify-between mb-2 mt-5 gap-y-7 gap-x-5">
        <View ref={menuIconRef}>
          <Button variant="outline" onPress={openMenu}>
            <ButtonText>Filter</ButtonText>
            <ButtonIcon as={FilterIcon} />
          </Button>
        </View>

        <FilterSummary
          floors={floors}
          selectedFloorIds={selectedFloorIds}
          selectedRoomIds={selectedRoomIds}
          unassignedSelected={unassignedSelected}
        />
      </View>

      <PopupMenu
        visible={showMenu}
        onClose={() => setShowMenu(false)}
        position={menuPosition}
      >
        <FilterMenu
          floors={floors}
          selectedFloorIds={selectedFloorIds}
          toggleFloor={toggleFloor}
          selectedRoomIds={selectedRoomIds}
          toggleRoom={toggleRoom}
          unassignedSelected={unassignedSelected}
          toggleUnassigned={handleToggleUnassigned}
        />
      </PopupMenu>

      <View className="flex-1 bg-background-50 rounded-2xl p-4 max-h-min">
        <ScrollView>
          <View className="flex-1 grid md:grid-cols-2 gap-4">
            <SectionCard
              title="Overdue Tasks"
              total={`${data?.total_overdue ?? 0}`}
              titleClassNames="text-warning-600"
              icon={AlertCircleIcon}
              strokeColor="text-warning-300"
            >
              {!data?.overdue_appointments.length ? (
                <NoItemsComponent
                  title="Nothing overdue"
                  subtitle="No overdue tasks"
                />
              ) : (
                data?.overdue_appointments.map((appointment) => {
                  return (
                    <OverdueTaskItem
                      key={appointment.id}
                      residents={appointment.residents}
                      appointment={appointment}
                      onRowPress={handleAppointmentRowPress}
                      handleMarkAsDone={handleMarkAsDone}
                      isOverdueCompleted={isCompleted[appointment.id!]}
                      allResidents={appointment.all_residents}
                    />
                  );
                })
              )}
            </SectionCard>
            <SectionCard
              title="Upcoming Tasks"
              total={`${data?.total_due_today ?? 0}`}
              titleClassNames="text-success-600"
              icon={ClockIcon}
              strokeColor="text-success-300"
            >
              {!data?.due_today_appointments.length ? (
                <NoItemsComponent
                  title="No upcoming tasks"
                  subtitle="There are no listed upcoming tasks"
                />
              ) : (
                data?.due_today_appointments.map((appointment) => {
                  return (
                    <UpcomingTaskItem
                      key={appointment.id}
                      residents={appointment.residents}
                      appointment={appointment}
                      onRowPress={handleAppointmentRowPress}
                      handleMarkAsDone={handleMarkAsDone}
                      isUpcomingCompleted={isCompleted[appointment.id!]}
                      allResidents={appointment.all_residents}
                    ></UpcomingTaskItem>
                  );
                })
              )}
            </SectionCard>
            <SectionCard
              title="Urgent Concerns"
              total={`${data?.urgent_concerns.length ?? 0}`}
              titleClassNames="text-error-700"
              icon={AlertCircleIcon}
              strokeColor="text-error-300"
            >
              {!data?.urgent_concerns.length ? (
                <NoItemsComponent
                  title="No urgent concerns"
                  subtitle="There are no listed urgent concerns"
                />
              ) : (
                data?.urgent_concerns.map((concern) => {
                  return (
                    <ConcernTaskItem
                      key={concern.id}
                      resident={concern.resident}
                      concern={concern}
                      onRowPress={handleUrgentConcernRowPress}
                      handleMarkAsDone={handleMarkConcernAsDone}
                      isConcernCompleted={isCompleted[concern.id!]}
                    ></ConcernTaskItem>
                  );
                })
              )}
            </SectionCard>
            <SectionCard
              title="Today's Appointments"
              total={`${data?.todays_appointments.length ?? 0}`}
              titleClassNames="text-typography-600"
              icon={CalendarDaysIcon}
              strokeColor="text-secondary-600"
            >
              {!data?.todays_appointments.length ? (
                <NoItemsComponent
                  title="Nothing due"
                  subtitle="No appointments are due today"
                />
              ) : (
                data?.todays_appointments.map((appointment) => {
                  return (
                    <TodayTaskItem
                      key={appointment.id}
                      residents={appointment.residents}
                      appointment={appointment}
                      onRowPress={handleAppointmentRowPress}
                      handleMarkAsDone={handleMarkAsDone}
                      isTodayCompleted={isCompleted[appointment.id!]}
                      allResidents={appointment.all_residents}
                    ></TodayTaskItem>
                  );
                })
              )}
            </SectionCard>
            <CalendarEventModal ref={ref} />
          </View>
        </ScrollView>
      </View>
    </View>
  );
}
